import { useMemo } from "react";

import { StringOptions } from "../../../components/Select";
import {
  Accounts,
  useGetAccountsQuery,
  useGetAccountsYearsQuery,
  useGetCategoriesQuery,
  useGetTransactionsTotalsQuery,
} from "../../../services";
import { TransactionType } from "../../../utils";
import { ALL_VALUE } from "../components/TransactionsFilters/constants";
import { Category } from "../types";

export interface UseTransactionsStaticDataReturn {
  accounts: Accounts[];
  categoriesIncome: Category[];
  categoriesExpense: Category[];
  accountOptions: StringOptions[];
  categoryOptions: StringOptions[];
  yearOptions: StringOptions[];
  totalCount: number;
  isLoading: boolean;
}

export const useTransactionsStaticData = (
  selectedType: string,
): UseTransactionsStaticDataReturn => {
  const { data: categoriesIncome = [], isLoading: isIncomeCategoriesLoading } =
    useGetCategoriesQuery(true);
  const {
    data: categoriesExpense = [],
    isLoading: isExpenseCategoriesLoading,
  } = useGetCategoriesQuery(false);

  const { data: accounts = [], isLoading: isAccountsLoading } =
    useGetAccountsQuery();
  const { data: availableYears = [], isLoading: isYearsLoading } =
    useGetAccountsYearsQuery();
  const { data: grandTotal, isLoading: isTotalLoading } =
    useGetTransactionsTotalsQuery({
      type: [TransactionType.ALL],
      searchBy: "",
      dateRange: "all",
    });

  const totalCount = grandTotal?.transactionsCount ?? 0;

  const yearOptions = useMemo<StringOptions[]>(
    () =>
      availableYears.map((year) => ({
        label: String(year),
        value: String(year),
      })),
    [availableYears],
  );

  const categoryOptions = useMemo<StringOptions[]>(() => {
    let source: Category[];
    if (selectedType === TransactionType.INCOME) {
      source = categoriesIncome;
    } else if (selectedType === TransactionType.EXPENSE) {
      source = categoriesExpense;
    } else {
      source = [...categoriesIncome, ...categoriesExpense];
    }
    const mapped = source.map((category) => ({
      label: category.label,
      value: category.value,
    }));
    return [{ label: "All", value: ALL_VALUE }, ...mapped];
  }, [selectedType, categoriesIncome, categoriesExpense]);

  const accountOptions = useMemo<StringOptions[]>(() => {
    const mapped = accounts.map((account) => ({
      label: account.name,
      value: String(account.id),
    }));
    return [{ label: "All", value: ALL_VALUE }, ...mapped];
  }, [accounts]);

  return {
    accounts,
    categoriesIncome,
    categoriesExpense,
    accountOptions,
    categoryOptions,
    yearOptions,
    totalCount,
    isLoading:
      isAccountsLoading ||
      isYearsLoading ||
      isTotalLoading ||
      isIncomeCategoriesLoading ||
      isExpenseCategoriesLoading,
  };
};
